/* ============================================================
   Velvet Rituals — AI Partner page shared JS
   assets/vr-ai.js

   Loaded (deferred) by snippets/vr-ai-assets.liquid from every
   vr-ai-* section. Self-guards against double execution.

   1. Scroll reveals        — .vrai-r → .in
   2. Chat conversation     — [data-vrai-chat] typing loop
   3. Pointer tilt          — [data-vrai-tilt] (fine pointers only)
   4. Stat counters         — [data-vrai-count]
   5. Journey progress line — [data-vrai-progress]
   ============================================================ */
(function () {
  'use strict';
  if (window.__vrAiInit) return;
  window.__vrAiInit = true;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── 1. Scroll reveals ─────────────────────────────── */
  var revealIO = null;
  if ('IntersectionObserver' in window && !REDUCED) {
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          revealIO.unobserve(e.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -36px 0px' });
  }

  function bindReveals(root) {
    var els = (root || document).querySelectorAll('.vrai-r:not(.in)');
    els.forEach(function (el) {
      if (revealIO) revealIO.observe(el);
      else el.classList.add('in');
    });
  }

  /* Hero elements are above the fold — reveal immediately */
  function revealHero(root) {
    var els = (root || document).querySelectorAll('.vrai__hero .vrai-r');
    els.forEach(function (el) {
      var m = el.className.match(/\bd(\d)\b/);
      var delay = m ? parseInt(m[1], 10) * 140 : 0;
      setTimeout(function () { el.classList.add('in'); if (revealIO) revealIO.unobserve(el); }, delay);
    });
  }

  /* ── 2. Chat conversation loop ─────────────────────── */
  /* Server renders every message (works with no JS). JS hides
     them, then replays the thread: typing dots → message,
     pausing between, and loops after a long beat. */
  function initChat(chat) {
    if (chat.__vrAiChat) return;
    chat.__vrAiChat = true;

    var msgs = Array.prototype.slice.call(chat.querySelectorAll('.vrai__msg'));
    if (msgs.length < 2 || REDUCED) return;

    var typing = chat.querySelector('.vrai__typing');
    var stream = chat.querySelector('.vrai__chat-stream');
    var idx = 0;
    var TYPE_MS  = 1050;  /* typing indicator dwell */
    var READ_MS  = 1350;  /* pause between messages */
    var LOOP_MS  = 5200;  /* rest before the thread replays */

    chat.classList.add('vrai__chat--js');

    function showTyping(msg, show) {
      if (!typing) return;
      typing.classList.toggle('on', show);
      typing.classList.toggle('vrai__typing--user', show && msg.classList.contains('vrai__msg--user'));
      if (show && stream) stream.scrollTop = stream.scrollHeight;
    }

    function step() {
      if (idx >= msgs.length) {
        setTimeout(function () {
          msgs.forEach(function (m) { m.classList.remove('on'); });
          idx = 0;
          setTimeout(step, 700);
        }, LOOP_MS);
        return;
      }
      var msg = msgs[idx];
      showTyping(msg, true);
      setTimeout(function () {
        showTyping(msg, false);
        msg.classList.add('on');
        if (stream) stream.scrollTop = stream.scrollHeight;
        idx += 1;
        setTimeout(step, READ_MS);
      }, TYPE_MS);
    }

    /* Start once the card is actually on screen */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { io.disconnect(); setTimeout(step, 600); }
        });
      }, { threshold: 0.35 });
      io.observe(chat);
    } else {
      setTimeout(step, 900);
    }
  }

  /* ── 3. Pointer tilt (desktop, fine pointer only) ──── */
  function initTilt(card) {
    if (card.__vrAiTilt || REDUCED) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    card.__vrAiTilt = true;

    var MAX = 5; /* degrees */
    var raf = null;

    card.addEventListener('pointermove', function (ev) {
      var r = card.getBoundingClientRect();
      var x = (ev.clientX - r.left) / r.width - 0.5;
      var y = (ev.clientY - r.top) / r.height - 0.5;
      if (raf) return;
      raf = requestAnimationFrame(function () {
        card.style.transform =
          'perspective(900px) rotateX(' + (-y * MAX).toFixed(2) + 'deg)' +
          ' rotateY(' + (x * MAX).toFixed(2) + 'deg) translateY(-4px)';
        raf = null;
      });
    });
    card.addEventListener('pointerleave', function () {
      card.style.transform = '';
    });
  }

  /* ── 4. Stat counters ──────────────────────────────── */
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  function animateCount(el) {
    var target = parseFloat(el.getAttribute('data-vrai-count'));
    if (isNaN(target)) return;
    var suffix = el.getAttribute('data-suffix') || '';
    var prefix = el.getAttribute('data-prefix') || '';
    if (REDUCED) { el.textContent = prefix + target + suffix; return; }
    var duration = 1500;
    var start = null;
    function stepFrame(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      el.textContent = prefix + Math.floor(easeOutCubic(p) * target) + suffix;
      if (p < 1) requestAnimationFrame(stepFrame);
      else el.textContent = prefix + target + suffix;
    }
    requestAnimationFrame(stepFrame);
  }

  function bindCounters(root) {
    var nums = (root || document).querySelectorAll('[data-vrai-count]');
    if (!nums.length) return;
    if ('IntersectionObserver' in window && !REDUCED) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { animateCount(e.target); io.unobserve(e.target); }
        });
      }, { threshold: 0.55 });
      nums.forEach(function (el) { io.observe(el); });
    } else {
      nums.forEach(animateCount);
    }
  }

  /* ── 5. Journey progress line ──────────────────────── */
  /* Fills the timeline as the steps scroll through the viewport.
     Works on either axis: a track wider than tall fills with
     scaleX (desktop 3-column), otherwise scaleY (vertical). */
  function initProgress(line) {
    if (line.__vrAiProg) return;
    line.__vrAiProg = true;
    if (REDUCED) { line.style.transform = 'none'; return; }

    var wrap = line.closest('[data-vrai-progress-wrap]') || line.parentElement;
    var ticking = false;

    function update() {
      ticking = false;
      var r = wrap.getBoundingClientRect();
      var track = line.parentElement.getBoundingClientRect();
      var horizontal = track.width > track.height;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var total = r.height + vh * 0.4;
      var passed = vh * 0.72 - r.top;
      var p = Math.max(0, Math.min(1, passed / total));
      line.style.transform = (horizontal ? 'scaleX(' : 'scaleY(') + p.toFixed(4) + ')';
    }
    function onScroll() {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }

  /* ── 6. Device-aware store links ───────────────────── */
  /* Android → Play Store; iOS and desktop keep the App Store
     href already rendered by Liquid. */
  function initStoreLink(link) {
    if (link.__vrAiStore) return;
    link.__vrAiStore = true;
    var ua = navigator.userAgent || '';
    if (/android/i.test(ua) && link.getAttribute('data-android')) {
      link.setAttribute('href', link.getAttribute('data-android'));
      link.setAttribute('aria-label', 'Get it on Google Play');
    }
  }

  /* ── Boot + theme-editor section reloads ───────────── */
  function boot(root) {
    bindReveals(root);
    revealHero(root);
    bindCounters(root);
    (root || document).querySelectorAll('[data-vrai-chat]').forEach(initChat);
    (root || document).querySelectorAll('[data-vrai-tilt]').forEach(initTilt);
    (root || document).querySelectorAll('[data-vrai-progress]').forEach(initProgress);
    (root || document).querySelectorAll('[data-vrai-store]').forEach(initStoreLink);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
})();
